import {
  createContext,
  ReactNode,
  useContext,
  useEffect,
  useState,
} from 'react'

import { FirebaseError } from 'firebase/app'
import {
  arrayRemove,
  arrayUnion,
  collection,
  deleteDoc,
  doc,
  getDoc,
  serverTimestamp,
  setDoc,
  updateDoc,
} from 'firebase/firestore'
import { deleteObject, ref } from 'firebase/storage'

import { challengeFields } from '../components/forms/ChallengeForm/challengeFields'
import { auth, firestore, storage } from '../firebase/firebaseConfig'
import { subscribeToCollection } from '../firebase/utils/collectionActions'
import { updateDocument } from '../firebase/utils/documentsActions'
import { useChaFormState } from '../hooks'
import { Challenge } from '../types'
import { handleErrors } from '../utilFunctions/firebaseErrors'

type Ctx = {
  challengeList: Challenge[]
  formActions: ReturnType<typeof useChaFormState>
  formState: Challenge
  saveNewChallenge: () => Promise<void>
  removeChallenge: (challengeId: string) => Promise<void>
  loadingChallenge: boolean
  error: string | null
  isEditing: boolean
  updateChallenge: () => Promise<void>
  updateParticipant: (studentId: string) => Promise<
    | {
        message: string
      }
    | undefined
  >
}

const ChallengeContext = createContext<Ctx | null>(null)

export const ChallengesProvider = ({
  initialState,
  editChallengeId,
  children,
}: {
  initialState: Challenge
  editChallengeId?: string
  children: ReactNode
}) => {
  const [challengeList, setChallengeList] = useState<Challenge[]>([])
  const [formState, setFormState] = useState<Challenge>(initialState)
  const [error, setError] = useState<null | string>(null)
  const [loadingChallenge, setLoadingChallenge] = useState<boolean>(false)
  const formActions = useChaFormState(formState, setFormState, challengeFields)
  const isEditing = Boolean(editChallengeId || initialState.id)

  useEffect(() => {
    let unsubscribeChallenges: (() => void) | undefined

    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const challengesRef = collection(firestore, 'challenges')
        unsubscribeChallenges = subscribeToCollection<Challenge>(
          challengesRef,
          setChallengeList,
        )
      }
    })

    return () => {
      unsubscribeChallenges?.()
      unsubscribe()
    }
  }, [])

  useEffect(() => {
    setFormState(initialState)
  }, [initialState])

  useEffect(() => {
    if (!editChallengeId) return
    const challengeToEdit = challengeList.find(
      (challenge) => challenge.id === editChallengeId,
    )
    if (challengeToEdit) {
      setFormState(challengeToEdit)
    }
  }, [challengeList, editChallengeId])

  const createNotification = (action: 'created' | 'updated') => {
    const title = action === 'created' ? 'Nuevo Desafío' : 'Desafío actualizado'
    const type =
      action === 'created' ? 'challenge_created' : 'challenge_updated'
    const body =
      action === 'created'
        ? `Se publicó “${formState?.title ?? 'Nuevo desafío'}”`
        : `${formState?.title ?? ''}`

    return {
      type,
      roleTarget: 'students',
      documentId: formState.id,
      title,
      body,
      createdAt: serverTimestamp(),
    }
  }

  const saveNewChallenge = async () => {
    setLoadingChallenge(true)
    setError(null)

    try {
      const challengesRef = collection(firestore, 'challenges')
      const notificationRef = doc(collection(firestore, 'notifications'))

      // si no viene id en el form, generamos uno nuevo
      const challengeRef = formState.id
        ? doc(challengesRef, formState.id)
        : doc(challengesRef)

      const payload: Challenge = {
        // si Challenge tiene más campos, agrégalos acá
        ...(formState as Challenge),
        id: challengeRef.id,
      }

      await setDoc(challengeRef, payload, { merge: true })

      const notif = createNotification('created')
      await setDoc(notificationRef, {
        ...notif,
        id: notificationRef.id,
      })

      // actualizamos el estado del form con el id definitivo
      setFormState(initialState)
    } catch (err) {
      const { code, message } = err as FirebaseError
      const friendly = handleErrors(code)
      setError(friendly || message)
      console.error('Error al guardar el challenge:', err)
    } finally {
      setLoadingChallenge(false)
    }
  }

  const removeChallenge = async (challengeId: string) => {
    setLoadingChallenge(true)
    setError(null)

    try {
      const challengeRef = doc(firestore, 'challenges', challengeId)
      const snap = await getDoc(challengeRef)

      if (!snap.exists()) {
        console.warn(`Challenge ${challengeId} no existe en Firestore`)
        return
      }

      const data = snap.data() as Challenge

      if (data.image) {
        const imgRef = ref(storage, data.image)
        await deleteObject(imgRef)
      }

      await deleteDoc(challengeRef)
    } catch (err) {
      const { code, message } = err as FirebaseError
      const friendly = handleErrors(code)
      setError(friendly || message)
      console.error('Error al eliminar el challenge:', err)
    } finally {
      setLoadingChallenge(false)
    }
  }

  const updateChallenge = async () => {
    if (!formState.id) {
      console.error('No hay Desafio activo')
      return
    }
    setLoadingChallenge(true)
    setError(null)

    try {
      const challengeRef = doc(firestore, 'challenges', formState.id)
      const notificationRef = doc(collection(firestore, 'notifications'))

      await updateDocument<Challenge>(challengeRef, formState)
      const notif = createNotification('updated')
      await setDoc(notificationRef, {
        ...notif,
        id: notificationRef.id,
      })
    } catch (error) {
      const { code, message } = error as FirebaseError
      const friendly = handleErrors(code)
      setError(friendly || message)
      console.error('Error al actualizar el challenge:', error)
    } finally {
      setLoadingChallenge(false)
    }
  }

  const updateParticipant = async (studentId: string) => {
    if (!formState.id) {
      console.error('No hay Desafio activo')
      return
    }
    const challengeRef = doc(firestore, 'challenges', formState.id)
    try {
      const docSnapshot = await getDoc(challengeRef)

      if (!docSnapshot.exists()) {
        return
      }

      const currentParticipants = (docSnapshot.data()?.participants ??
        []) as string[]

      const exists = currentParticipants.includes(studentId)

      await updateDoc(challengeRef, {
        participants: exists ? arrayRemove(studentId) : arrayUnion(studentId),
      })

      return { message: `Participante ${exists ? 'eliminado' : 'agregado'}` }
    } catch (error) {
      const { code, message } = error as FirebaseError
      const friendly = handleErrors(code)
      setError(friendly || message)
      console.error('Error al actualizar los participantes:', error)
    }
  }

  return (
    <ChallengeContext.Provider
      value={{
        challengeList,
        formActions,
        formState,
        saveNewChallenge,
        removeChallenge,
        loadingChallenge,
        error,
        isEditing,
        updateChallenge,
        updateParticipant,
      }}
    >
      {children}
    </ChallengeContext.Provider>
  )
}

export const useChallenge = () => {
  const ctx = useContext(ChallengeContext)
  if (!ctx) throw new Error('useChallengeForm must be used within provider')
  return ctx
}
